import {useSelector} from 'react-redux';
import {TShow} from '../../hooks/useShow';
import {TState} from '../../redux/store.type';
import AIMessageGenerator from '../aiMessageGenerator/AIMessageGenerator';
import type {AssistantDraftPayload, ChatMessage} from '../aiMessageGenerator/utils/types';
import {mapChatMessagesToApiHistory} from '../aiMessageGenerator/utils/types';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {TemplateStructure} from '../whatsAppTemplateEditor/types/templateStructure';
import {getWhatsAppAIDraft, updateWhatsAppMessage} from '../../redux/branding/brandingActions';
import useRestartXHR from '../../hooks/useRestartXHR';
import {prepareParameterValuesForSave} from '../whatsAppTemplateEditor/hooks/useTemplateStructure';

export interface AIWhatsappGeneratorProps {
    show: TShow;
    messageType: string;
    updateMessage: (structure: TemplateStructure) => void;
    persistOnApprove?: boolean;
    container?: 'sidePopup' | 'innerPop';
}
export default function AIWhatsappGenerator({
    show,
    messageType,
    updateMessage,
    persistOnApprove = true,
    container = 'sidePopup',
}: AIWhatsappGeneratorProps) {
    const {t} = useTranslation('whatsapp');
    const eid = useSelector((s: TState) => s.event?.eid);
    const draftXHR = useSelector((s: TState) => s.branding?.getWhatsAppAIDraftXHR);
    const approveXHR = useSelector((s: TState) => s.branding?.saveWhatsAppAIDraftFullXHR);
    const suggestions = useMemo(() => {
        const raw = t('aiGenerator.suggestions', {returnObjects: true}) as unknown;
        return Array.isArray(raw) ? raw as string[] : [];
    }, [t]);
    
    const handleAiDraft = useCallback(
        (prompt: string, history: ChatMessage[]) =>
            getWhatsAppAIDraft(prompt, messageType, mapChatMessagesToApiHistory(history)),
        [messageType]
    );
    const handleAiApprove = useCallback(async (draft: AssistantDraftPayload) => {
        const parameterValues = draft?.parameterValues;
        if(!parameterValues?.length) {
            return;
        }
        if(persistOnApprove) {
            const structure = prepareParameterValuesForSave(parameterValues);
            await updateWhatsAppMessage(structure, messageType, null);
        }
        updateMessage(parameterValues);
    }, [messageType, updateMessage, persistOnApprove]);
    useRestartXHR(draftXHR, 'branding.getWhatsAppAIDraftXHR');
    useRestartXHR(approveXHR, 'branding.saveWhatsAppAIDraftFullXHR');

    const analytics = useMemo(() => (eid ? {
        contentId: eid,
        context: {message_type: messageType, feature: 'whatsapp'},
    } : undefined), [eid, messageType]);

    return (
        <AIMessageGenerator
            show={show}
            title={t('aiGenerator.title')}
            placeholder={t('aiGenerator.placeholder')}
            errorMessage={t('aiGenerator.errorMessage')}
            submit={{label: t('aiGenerator.submitLabel'), onClick: handleAiDraft, xhrState: draftXHR}}
            suggestions={suggestions}
            approve={{label: t('aiGenerator.approveLabel'), onClick: handleAiApprove, xhrState: persistOnApprove ? approveXHR : undefined}}
            analytics={analytics}
            container={container}
        />
    );
}
